import { ChevronRight, Play } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { metaFor, timeAgo, splitActor } from "@/lib/notifMeta";

/** Instagram-style notification row: actor avatar · bold actor + action · time · content preview thumbnail. */
export default function NotificationItem({ n, onClick, dense = false, testidPrefix = "notif" }) {
  const meta = metaFor(n.type);
  const Icon = meta.icon;
  const { actor, rest } = splitActor(n);
  const d = n.data || {};
  const hasActor = !!d.actor_id;
  const previewImage = d.preview_image;
  const previewVideo = d.preview_video;
  const previewText = d.comment_text || d.preview_text;

  return (
    <button
      onClick={() => onClick?.(n)}
      data-testid={`${testidPrefix}-${n.notif_id}`}
      data-notif-type={n.type}
      data-read={n.read ? "1" : "0"}
      className={`w-full text-left ${dense ? "p-3" : "px-4 py-3"} border-b border-border/50 hover:bg-muted/50 transition-colors flex items-center gap-3 ${!n.read ? "bg-primary/5" : ""}`}
    >
      <div className="relative shrink-0">
        {hasActor ? (
          <Avatar className={dense ? "w-10 h-10" : "w-11 h-11"}>
            <AvatarImage src={d.actor_picture} />
            <AvatarFallback>{(d.actor_name || "?")[0]}</AvatarFallback>
          </Avatar>
        ) : (
          <div className={`${dense ? "w-10 h-10" : "w-11 h-11"} rounded-full flex items-center justify-center ${meta.tint}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
        {hasActor && (
          <span className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-2 border-background flex items-center justify-center ${meta.tint}`}>
            <Icon className="w-2.5 h-2.5" />
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm leading-snug break-words">
          {actor ? <><span className="font-semibold">{actor}</span>{rest}</> : <span className={dense ? "whitespace-pre-wrap" : "line-clamp-2 whitespace-pre-wrap"}>{rest}</span>}
          <span className="text-muted-foreground text-xs ml-1.5">{timeAgo(n.created_at)}</span>
        </div>
        {previewText && hasActor && (
          <div className="text-xs text-muted-foreground truncate mt-0.5" data-testid={`${testidPrefix}-preview-${n.notif_id}`}>“{previewText}”</div>
        )}
        {!hasActor && <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{meta.label}</div>}
      </div>
      {previewImage ? (
        <img src={previewImage} alt="" className="w-11 h-11 rounded-md object-cover shrink-0 border border-border" data-testid={`${testidPrefix}-thumb-${n.notif_id}`} />
      ) : previewVideo ? (
        <div className="w-11 h-11 rounded-md bg-black/80 flex items-center justify-center shrink-0" data-testid={`${testidPrefix}-thumb-${n.notif_id}`}>
          <Play className="w-4 h-4 text-white" />
        </div>
      ) : (
        <div className="flex items-center gap-1 shrink-0">
          {!n.read && <span className="w-2 h-2 rounded-full bg-primary" />}
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
      )}
    </button>
  );
}
